import '@/styles/globals.css';
import '@/styles/fonts.css';
import type { AppProps } from 'next/app';
import { SessionProvider } from "next-auth/react";
import { TextEditorDrawerProvider } from '@/contexts/TextEditorDrawerContext';
import { SelectedUserAccountProvider } from '@/contexts/SelectedUserAccountContext';
import { UsageProvider } from '@/contexts/UsageContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Head from 'next/head';
import React, { useEffect } from 'react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import * as amplitude from '@amplitude/analytics-browser';
import { useRouter } from 'next/router';
import 'nprogress/nprogress.css'; // This is the default nprogress style
import NProgress from 'nprogress'; // Import nprogress
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import Script from 'next/script';

let ErrorBoundary = ({ children }: { children: React.ReactNode }) => <>{children}</>;

if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
  const Bugsnag = require('@bugsnag/js').default;
  const BugsnagPluginReact = require('@bugsnag/plugin-react').default;

  Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact(React)],
    autoTrackSessions: false,
    collectUserIp: false,
  });

  const ErrorBoundaryBugsnag = Bugsnag.getPlugin('react')!.createErrorBoundary(React);
  ErrorBoundary = ErrorBoundaryBugsnag;
}

if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_APP_ENV === 'production') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
    capture_pageview: true,
    disable_session_recording: false,
    enable_recording_console_log: false,
    persistence: 'localStorage+cookie',
    loaded: (ph) => {
      ph.debug(false); // silence logs in prod unless needed
    },
  });
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  /*
  useEffect(() => {
    const handleStart = (url: string) => NProgress.start();
    const handleStop = () => NProgress.done();

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);
  */

  /*
  useEffect(() => {
    // Check if the environment is production
    if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
      amplitude.init('cf8589c1caf0ff8e69d88d41ac4f5ada');

      // Function to track page views
      const trackPageView = (url: string) => {
        amplitude.track('PAGE_VIEW', {
          path: url,
        });
      };

      // Track the initial page load
      trackPageView(window.location.pathname);

      // Track page changes after the initial render
      router.events.on('routeChangeComplete', trackPageView);

      // Cleanup subscription on unmount
      return () => {
        router.events.off('routeChangeComplete', trackPageView);
      };
    }
  }, [router.events]);
  */

  return (
    <SessionProvider session={pageProps.session}>
      <UsageProvider>
        <TextEditorDrawerProvider>
          <SelectedUserAccountProvider>
            <Head>
              <link rel="icon" href="/images/logo-icon.png" />
              <title>Scriptu</title>
            </Head>

            {process.env.NEXT_PUBLIC_APP_ENV === 'production' && (
              <>
                {/* Google Analytics */}
                <Script
                  async
                  src="https://www.googletagmanager.com/gtag/js?id=AW-994078652"
                />
                <Script
                  id="google-analytics"
                  dangerouslySetInnerHTML={{
                    __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', 'AW-994078652');
                `,
                  }}
                />

                {/* MS Clarity */}
                <Script
                  id="microsoft-clarity"
                  strategy="afterInteractive"
                  dangerouslySetInnerHTML={{
                    __html: `
                  (function(c,l,a,r,i,t,y){
                      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                  })(window, document, "clarity", "script", "r1z5wk9lll");
                `,
                  }}
                />

                {/* Facebook Pixel */}
                <Script
                  id="facebook-pixel"
                  strategy="afterInteractive"
                  dangerouslySetInnerHTML={{
                    __html: `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '4392817664286529');
                fbq('track', 'PageView');
              `,
                  }}
                />
                <noscript>
                  <img
                    height="1"
                    width="1"
                    style={{ display: 'none' }}
                    src="https://www.facebook.com/tr?id=4392817664286529&ev=PageView&noscript=1"
                  />
                </noscript>
              </>
            )}


            {process.env.NEXT_PUBLIC_APP_ENV === 'staging' && (
              <div style={{ backgroundColor: '#ffc107', padding: '3px 0px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>
                You are in staging environment
              </div>
            )}
            <ErrorBoundary>
              <PostHogProvider client={posthog}>
                <Component {...pageProps} />
              </PostHogProvider>
            </ErrorBoundary>
          </SelectedUserAccountProvider>
        </TextEditorDrawerProvider>
      </UsageProvider>

      <ToastContainer
        position="top-center"
        autoClose={1500}
        hideProgressBar={false}
        newestOnTop={false}
        draggable={false}
        pauseOnHover
        style={{ zIndex: 9999999, fontWeight: 600 }}
      />
    </SessionProvider>
  );
}
